import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { useEscapeTwiceToExit } from "@/hooks/useQuickExit";
import { PublicLayout } from "@/components/PublicLayout";
import { RequireAuth } from "@/components/RequireAuth";
import HomePage from "@/pages/public/HomePage";
import HowItWorksPage from "@/pages/public/HowItWorksPage";
import { WhoCanReceiveCarePage, OurCounselorsPage, LifeCareClassesPage, PrayWithUsPage } from "@/pages/public/InfoPages";
import ReachOutPage from "@/pages/public/ReachOutPage";
import StaffReachOut from "@/pages/staff/StaffReachOut";
import ResourcesPage from "@/pages/public/ResourcesPage";
import CrisisPage from "@/pages/public/CrisisPage";
import FaqPage from "@/pages/public/FaqPage";
import { PrivacyPage, TermsPage, SmsTermsPage, AccessibilityPage } from "@/pages/public/LegalPages";
import NotFoundPage from "@/pages/public/NotFoundPage";
import SignUpPage from "@/pages/auth/SignUpPage";
import SignInPage from "@/pages/auth/SignInPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/auth/PasswordPages";
import MfaSetupPage from "@/pages/auth/MfaSetupPage";
import { PortalLayout } from "@/pages/portal/PortalLayout";
import PortalHome from "@/pages/portal/PortalHome";
import PortalPreferences from "@/pages/portal/PortalPreferences";
import CareIntakePage from "@/pages/portal/CareIntakePage";
import { StaffLayout } from "@/pages/staff/StaffLayout";
import StaffDashboard from "@/pages/staff/StaffDashboard";
import StaffPeople from "@/pages/staff/StaffPeople";
import StaffAuditLog from "@/pages/staff/StaffAuditLog";
import StaffSettings from "@/pages/staff/StaffSettings";
import StaffContent from "@/pages/staff/StaffContent";
import StaffResources from "@/pages/staff/StaffResources";
import StaffOutbox from "@/pages/staff/StaffOutbox";
import StaffPlaceholder from "@/pages/staff/StaffPlaceholder";
import StaffUrgent from "@/pages/staff/StaffUrgent";
import StaffIntake from "@/pages/staff/StaffIntake";
import StaffCases from "@/pages/staff/StaffCases";
import StaffCaseDetail from "@/pages/staff/StaffCaseDetail";
import StaffForms from "@/pages/staff/StaffForms";
import StaffVideos from "@/pages/staff/StaffVideos";
import StaffProviders from "@/pages/staff/StaffProviders";
import StaffProviderEdit from "@/pages/staff/StaffProviderEdit";
import PortalVideos from "@/pages/portal/PortalVideos";
import InPersonIntakePage from "@/pages/portal/InPersonIntakePage";
import VerifyPage from "@/pages/public/VerifyPage";
import JoinPage from "@/pages/public/JoinPage";
import PortalAppointments from "@/pages/portal/PortalAppointments";
import PortalMessages from "@/pages/portal/PortalMessages";
import StaffCalendar from "@/pages/staff/StaffCalendar";
import StaffMessages from "@/pages/staff/StaffMessages";

const STAFF_ROLES = ["counselor", "supervisor", "care_coordinator", "class_facilitator", "care_director", "content_editor", "system_admin"];

function Shell() {
  useEscapeTwiceToExit();
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/who-can-receive-care" element={<WhoCanReceiveCarePage />} />
        <Route path="/our-counselors" element={<OurCounselorsPage />} />
        <Route path="/life-care-classes" element={<LifeCareClassesPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/pray-with-us" element={<PrayWithUsPage />} />
        <Route path="/reach-out" element={<ReachOutPage />} />
        <Route path="/crisis" element={<CrisisPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/sms-terms" element={<SmsTermsPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/verify/:number" element={<VerifyPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account/security" element={<RequireAuth><MfaSetupPage /></RequireAuth>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="/portal" element={<RequireAuth><PortalLayout /></RequireAuth>}>
        <Route index element={<PortalHome />} />
        <Route path="preferences" element={<PortalPreferences />} />
        <Route path="care-intake" element={<CareIntakePage />} />
        <Route path="videos" element={<PortalVideos />} />
        <Route path="in-person-intake" element={<InPersonIntakePage />} />
        <Route path="appointments" element={<PortalAppointments />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="security" element={<MfaSetupPage embedded />} />
      </Route>
      <Route path="/staff" element={<RequireAuth roles={STAFF_ROLES} staff><StaffLayout /></RequireAuth>}>
        <Route index element={<StaffDashboard />} />
        <Route path="people" element={<StaffPeople />} />
        <Route path="urgent" element={<StaffUrgent />} />
        <Route path="intake" element={<StaffIntake />} />
        <Route path="cases" element={<StaffCases />} />
        <Route path="cases/:id" element={<StaffCaseDetail />} />
        <Route path="forms" element={<StaffForms />} />
        <Route path="videos" element={<StaffVideos />} />
        <Route path="providers" element={<StaffProviders />} />
        <Route path="providers/:userId" element={<StaffProviderEdit />} />
        <Route path="calendar" element={<StaffCalendar />} />
        <Route path="messages" element={<StaffMessages />} />
        <Route path="audit" element={<StaffAuditLog />} />
        <Route path="settings" element={<StaffSettings />} />
        <Route path="content" element={<StaffContent />} />
        <Route path="resources" element={<StaffResources />} />
        <Route path="communications" element={<StaffOutbox />} />
        <Route path="reach-out" element={<StaffReachOut />} />
        <Route path="dashboard" element={<Navigate to="/staff" replace />} />
        <Route path=":section" element={<StaffPlaceholder />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-center" richColors={false} toastOptions={{ className: "font-sans" }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
